import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useTheme } from "../App";
import { api } from "../App";
import {
  Sparkles,
  Copy,
  Clock,
  TrendingUp,
  Sun,
  Moon,
  ArrowRight,
  CheckCircle2,
  Code,
  Languages,
  Newspaper,
  Star,
  Shield,
  Linkedin,
  Mail,
} from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      try {
        const response = await api.get("/auth/me");
        if (response.data) {
          if (response.data.has_completed_onboarding) {
            navigate("/feed", { replace: true });
          } else {
            navigate("/onboarding", { replace: true });
          }
        }
      } catch {
        // Not authenticated, stay on landing page
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = encodeURIComponent(window.location.origin);
    const scope = encodeURIComponent("openid email profile");
    const responseType = "id_token";
    const nonce = Math.random().toString(36).substring(2);

    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?prompt=consent&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&response_type=${responseType}&nonce=${nonce}`;
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const GoogleSignInButton = ({ testId, className = "" }) => (
    <button
      onClick={handleGoogleLogin}
      data-testid={testId}
      className={`inline-flex items-center gap-3 px-6 py-3 bg-white border border-gray-300 rounded-full shadow-sm hover:shadow-md hover:bg-gray-50 transition-all text-sm font-semibold text-gray-700 ${className}`}
    >
      <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
      Sign in with Google
    </button>
  );

  return (
    <div className="min-h-screen bg-background font-body">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-panel bg-background/80 border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="PostWriter" className="w-8 h-8" />
              <span className="text-xl font-extrabold tracking-tighter font-headline text-foreground">
                PostWriter
              </span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="font-headline font-bold tracking-tight text-xs uppercase text-muted-foreground hover:text-primary transition-colors">Features</a>
              <a href="#how-it-works" className="font-headline font-bold tracking-tight text-xs uppercase text-muted-foreground hover:text-primary transition-colors">How it Works</a>
              <a href="#testimonials" className="font-headline font-bold tracking-tight text-xs uppercase text-muted-foreground hover:text-primary transition-colors">Testimonials</a>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
              >
                {theme === "dark" ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </Button>
              <button
                onClick={handleGoogleLogin}
                data-testid="header-login-btn"
                className="editorial-gradient text-white px-6 py-2 rounded-full font-headline font-bold text-sm uppercase tracking-wider shadow-lg hover:shadow-primary/20 transition-all active:scale-95"
              >
                Start Free
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-16">
        {/* Hero Section */}
        <section className="relative px-6 md:px-12 py-20 lg:py-32 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 z-10">
              <div className="inline-block px-4 py-1.5 mb-6 rounded-full bg-secondary text-muted-foreground font-headline text-[10px] font-extrabold uppercase tracking-[0.2em]">
                The Executive Edge
              </div>
              <h1 className="font-headline text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-[-0.03em] leading-[1.05] text-foreground mb-8">
                AI-Powered LinkedIn Content for{" "}
                <span className="text-primary italic">Tech Leaders.</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed mb-10">
                Stop struggling for ideas. Get daily, tech-tailored LinkedIn post
                suggestions you can copy, paste, and post in seconds.
              </p>
              <div className="max-w-md">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    size="lg"
                    onClick={handleGoogleLogin}
                    data-testid="hero-login-btn"
                    className="editorial-gradient text-white px-8 py-6 rounded-full font-headline font-bold text-sm uppercase tracking-widest whitespace-nowrap shadow-xl hover:brightness-110 transition-all active:scale-95"
                  >
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <p className="mt-4 text-xs text-muted-foreground font-medium">
                  Join 2,500+ CTOs and Tech Founders scaling their personal brand.
                </p>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative z-10 bg-card rounded-[2rem] shadow-2xl overflow-hidden ring-1 ring-border/20 transform lg:translate-x-6 rotate-2 hover:rotate-0 transition-transform duration-700">
                <div className="bg-muted p-4 flex items-center gap-2 border-b border-border/30">
                  <div className="w-3 h-3 rounded-full bg-red-300/40"></div>
                  <div className="w-3 h-3 rounded-full bg-primary/20"></div>
                  <div className="w-3 h-3 rounded-full bg-orange-300/30"></div>
                  <div className="ml-4 h-6 w-48 bg-muted-foreground/10 rounded-full"></div>
                </div>
                <div className="p-8">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full editorial-gradient flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-primary uppercase tracking-tighter">
                          Drafting Strategy
                        </div>
                        <div className="text-sm font-semibold text-foreground">
                          The Future of Edge Computing
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-xl text-sm leading-relaxed text-muted-foreground font-medium">
                      "Edge computing isn't just a trend; it's the decentralized
                      backbone of the next AI wave. Here's why every CTO needs to
                      shift their focus..."
                    </div>
                    <button className="w-full py-3 bg-primary/5 text-primary border border-primary/20 rounded-lg font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2">
                      <Copy className="w-3.5 h-3.5" />
                      Copy to LinkedIn
                    </button>
                  </div>
                </div>
              </div>
              <div className="absolute -top-10 -right-10 w-64 h-64 editorial-gradient opacity-10 rounded-full blur-3xl -z-0"></div>
            </div>
          </div>
        </section>

        {/* Social Proof Strip */}
        <section className="bg-muted/50 py-12 mb-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <p className="text-center font-headline text-[10px] font-extrabold uppercase tracking-[0.3em] text-muted-foreground mb-8">
              Trusted by leadership at
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-30">
              {["Google", "Meta", "Stripe", "Vercel"].map((name) => (
                <span
                  key={name}
                  className="text-lg font-headline font-extrabold tracking-tight text-muted-foreground"
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Bento Feature Grid */}
        <section id="features" className="max-w-7xl mx-auto px-6 md:px-12 pb-32">
          <div className="mb-16">
            <h2 className="font-headline text-3xl md:text-4xl font-extrabold tracking-tight mb-4">
              Master LinkedIn without the grind.
            </h2>
            <div className="w-20 h-1 editorial-gradient rounded-full"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Wide card — Tech-Tailored Trends */}
            <div className="md:col-span-2 bg-muted/40 dark:bg-card rounded-[2rem] p-10 flex flex-col justify-between overflow-hidden relative group">
              <div>
                <TrendingUp className="w-9 h-9 text-primary mb-6" />
                <h3 className="font-headline text-2xl font-bold mb-4">
                  Tech-Tailored Trends
                </h3>
                <p className="text-muted-foreground max-w-sm leading-relaxed">
                  Our AI scans GitHub trends, HN top stories, and tech whitepapers
                  to generate content that sounds like a leader, not a bot.
                </p>
              </div>
              <div className="mt-8 grid grid-cols-3 gap-3">
                {["AI/ML", "DevOps", "Frontend", "Cloud", "Security", "Data"].map(
                  (tag) => (
                    <span
                      key={tag}
                      className="px-3 py-2 bg-background rounded-xl text-xs font-semibold text-muted-foreground text-center"
                    >
                      {tag}
                    </span>
                  )
                )}
              </div>
            </div>

            {/* Stat card */}
            <div className="editorial-gradient text-white rounded-[2rem] p-10 flex flex-col justify-center text-center">
              <div className="text-5xl font-extrabold font-headline mb-2">93%</div>
              <p className="font-headline text-xs font-bold uppercase tracking-widest opacity-80 mb-6">
                Average Engagement Boost
              </p>
              <p className="text-sm leading-relaxed opacity-90">
                Users report nearly double the reach within the first 30 days of
                consistent posting.
              </p>
            </div>

            {/* Small card — 3 Minutes a Day */}
            <div className="bg-muted/40 dark:bg-card rounded-[2rem] p-10 flex flex-col justify-between">
              <div>
                <Clock className="w-9 h-9 text-foreground mb-6" />
                <h3 className="font-headline text-xl font-bold mb-4">
                  3 Minutes a Day
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Spend your morning coffee picking a prompt, tweaking a line, and
                  hitting post. Total authority, zero friction.
                </p>
              </div>
              <div className="mt-10 h-32 bg-muted rounded-2xl flex items-center justify-center">
                <Clock className="w-8 h-8 text-muted-foreground/40" />
              </div>
            </div>

            {/* Wide card — Your Voice, Amplified */}
            <div className="md:col-span-2 bg-card rounded-[2rem] p-10 shadow-[0_4px_24px_rgba(0,0,0,0.04)] ring-1 ring-border/10 flex flex-col md:flex-row gap-10 items-center">
              <div className="flex-1">
                <h3 className="font-headline text-2xl font-bold mb-4">
                  Your Voice, Amplified
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Upload your previous posts or blog articles. Our AI learns your
                  unique tone and mirrors it perfectly across every generated draft.
                </p>
                <a
                  href="#how-it-works"
                  className="text-primary font-bold text-sm uppercase tracking-wider flex items-center gap-2 group/link"
                >
                  Learn About Voice Mirroring
                  <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                </a>
              </div>
              <div className="flex-1 w-full">
                <div className="bg-muted/50 rounded-xl p-4 space-y-3">
                  <div className="h-2 w-1/2 bg-primary/20 rounded-full"></div>
                  <div className="h-2 w-full bg-muted-foreground/10 rounded-full"></div>
                  <div className="h-2 w-3/4 bg-muted-foreground/10 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="py-24 px-6 md:px-12 max-w-7xl mx-auto">
          <div className="mb-20">
            <span className="inline-block px-4 py-1.5 rounded-full bg-secondary text-muted-foreground font-body text-xs font-bold tracking-widest uppercase mb-6">
              Process
            </span>
            <h2 className="font-headline text-4xl md:text-5xl font-extrabold tracking-tighter leading-tight max-w-3xl">
              High-conversion authority,{" "}
              <span className="text-primary italic">automated.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
            {[
              {
                step: "01",
                title: "AI analyzes your tech interests.",
                desc: "Our engine deep-dives into your preferred repositories, stacks, and tech niches to build a unique semantic profile.",
              },
              {
                step: "02",
                title: "Get 5-10 tailored post suggestions daily.",
                desc: "Receive editorial-grade content ideas that bridge the gap between complex engineering and human engagement.",
              },
              {
                step: "03",
                title: "Copy, paste, and maintain presence.",
                desc: "Effortlessly maintain your digital authority across all professional platforms without losing hours to writing.",
              },
            ].map((item, idx) => (
              <div key={idx} className="group">
                <div className="mb-8 flex items-baseline gap-4">
                  <span className="text-6xl font-headline font-extrabold text-muted/80 group-hover:text-primary/30 transition-colors duration-500">
                    {item.step}
                  </span>
                  <div className="h-px flex-grow bg-border group-hover:bg-primary transition-colors duration-500"></div>
                </div>
                <h3 className="font-headline text-2xl font-bold mb-4">
                  {item.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed text-lg">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Capabilities Showcase */}
        <section className="bg-muted/30 py-24 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
              <div className="max-w-2xl">
                <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-body text-xs font-bold tracking-widest uppercase mb-6">
                  Capabilities
                </span>
                <h2 className="font-headline text-4xl md:text-5xl font-extrabold tracking-tighter leading-tight">
                  Tech-Tailored Insights
                </h2>
              </div>
              <p className="text-muted-foreground text-lg max-w-sm">
                Engineered specifically for developers and tech leaders who demand
                precision and professional aesthetics.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              {/* Code Snippet Formatting */}
              <div className="md:col-span-8 bg-card p-10 rounded-3xl flex flex-col md:flex-row gap-10 items-center overflow-hidden shadow-[0_12px_40px_rgba(25,28,30,0.06)] group">
                <div className="flex-1 order-2 md:order-1">
                  <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-8">
                    <Code className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-headline text-3xl font-bold mb-4">
                    Code Snippet Formatting
                  </h3>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                    Beautiful, syntactically accurate code presentation that honors
                    your technical depth while maintaining editorial polish.
                  </p>
                  <div className="flex gap-2">
                    <span className="px-3 py-1 bg-muted rounded-full text-xs font-semibold text-muted-foreground uppercase tracking-tight">
                      Syntax Highlighting
                    </span>
                    <span className="px-3 py-1 bg-muted rounded-full text-xs font-semibold text-muted-foreground uppercase tracking-tight">
                      Multi-Language
                    </span>
                  </div>
                </div>
                <div className="flex-1 order-1 md:order-2 w-full">
                  <div className="bg-gray-900 dark:bg-gray-950 rounded-2xl p-6 font-mono text-sm text-gray-100 shadow-lg transform group-hover:-translate-y-2 transition-transform duration-500">
                    <div className="flex gap-2 mb-4">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <span className="text-blue-400">const</span>{" "}
                        <span className="text-orange-300">aiPulse</span> = () =&gt;{" "}
                        {"{"}
                      </div>
                      <div className="pl-4 text-gray-500">
                        // Analyzing tech trends
                      </div>
                      <div className="pl-4">
                        <span className="text-blue-400">return</span>{" "}
                        pulse.generate();
                      </div>
                      <div>{"}"}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dev-to-Human Translation */}
              <div className="md:col-span-4 editorial-gradient text-white p-10 rounded-3xl flex flex-col justify-between shadow-[0_20px_50px_rgba(0,76,202,0.15)] group">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-8">
                  <Languages className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="font-headline text-3xl font-bold mb-4">
                    Dev-to-Human Translation
                  </h3>
                  <p className="text-white/80 text-lg leading-relaxed">
                    Turn complex architectural decisions into high-impact narratives
                    that resonate with stakeholders and peers alike.
                  </p>
                </div>
              </div>

              {/* Latest Tech News Integration */}
              <div className="md:col-span-12 bg-card p-10 rounded-3xl grid grid-cols-1 lg:grid-cols-2 gap-12 items-center shadow-[0_12px_40px_rgba(25,28,30,0.06)] group overflow-hidden">
                <div>
                  <div className="w-14 h-14 bg-secondary rounded-2xl flex items-center justify-center mb-8">
                    <Newspaper className="w-7 h-7 text-muted-foreground" />
                  </div>
                  <h3 className="font-headline text-3xl font-bold mb-4">
                    Latest Tech News Integration
                  </h3>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                    Real-time syncing with major tech publications and GitHub trends.
                    Always be the first to share insights on the next big framework
                    or security update.
                  </p>
                  <a
                    href="#features"
                    className="inline-flex items-center gap-2 text-primary font-bold group/link"
                  >
                    Explore Sources
                    <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
                  </a>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {["GitHub Trending", "Hacker News", "TechCrunch", "Dev.to"].map(
                    (source, i) => (
                      <div
                        key={source}
                        className={`h-28 bg-muted/50 rounded-2xl flex items-center justify-center p-6 hover:bg-muted transition-all ${
                          i % 2 !== 0 ? "mt-6" : ""
                        }`}
                      >
                        <span className="text-sm font-headline font-bold text-muted-foreground">
                          {source}
                        </span>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="py-24 px-6 md:px-12 max-w-7xl mx-auto text-center">
          <span className="inline-block font-body text-primary font-bold tracking-[0.2em] uppercase text-xs mb-4">
            Trusted by Industry Leaders
          </span>
          <h2 className="font-headline text-4xl md:text-5xl font-extrabold tracking-tighter mb-16 text-foreground">
            Join 10,000+ tech pros growing their LinkedIn.
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-5 group">
              <div className="bg-card p-8 md:p-10 rounded-xl shadow-[0_12px_40px_rgba(25,28,30,0.06)] ring-1 ring-border/10 transform transition-all duration-300 group-hover:-translate-y-2 text-left">
                <div className="flex items-center gap-1 text-yellow-500 mb-6">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-5 h-5 fill-current" />
                  ))}
                </div>
                <p className="text-lg md:text-xl leading-relaxed text-muted-foreground mb-8 italic">
                  "The AI's ability to capture my technical voice while maintaining
                  high engagement is unmatched. My network has grown by 40% in just
                  two months."
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full editorial-gradient flex items-center justify-center text-white font-bold text-sm">
                    MC
                  </div>
                  <div>
                    <div className="font-bold text-foreground">Marcus Chen</div>
                    <div className="text-sm text-muted-foreground font-medium">
                      CTO at TechScale
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="hidden lg:flex lg:col-span-2 justify-center items-center py-20">
              <div className="h-24 w-[2px] bg-gradient-to-b from-primary/40 to-transparent"></div>
            </div>
            <div className="lg:col-span-5 lg:mt-24 group">
              <div className="bg-card p-8 md:p-10 rounded-xl shadow-[0_12px_40px_rgba(25,28,30,0.06)] ring-1 ring-border/10 transform transition-all duration-300 group-hover:-translate-y-2 text-left">
                <div className="flex items-center gap-1 text-yellow-500 mb-6">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-5 h-5 fill-current" />
                  ))}
                </div>
                <p className="text-lg md:text-xl leading-relaxed text-muted-foreground mb-8 italic">
                  "I used to spend hours struggling with content. Now, PostWriter
                  handles the heavy lifting so I can focus on building products."
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center text-muted-foreground font-bold text-sm">
                    SJ
                  </div>
                  <div>
                    <div className="font-bold text-foreground">Sarah Jenkins</div>
                    <div className="text-sm text-muted-foreground font-medium">
                      Senior Dev at CloudEngine
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="relative overflow-hidden rounded-[2rem] bg-gray-900 dark:bg-gray-950 p-12 md:p-24 text-center">
              <div className="absolute top-0 right-0 -mr-24 -mt-24 w-96 h-96 bg-primary/20 rounded-full blur-[100px]"></div>
              <div className="absolute bottom-0 left-0 -ml-24 -mb-24 w-96 h-96 bg-primary/10 rounded-full blur-[100px]"></div>
              <div className="relative z-10 max-w-3xl mx-auto">
                <h2 className="font-headline text-4xl md:text-6xl font-extrabold tracking-tighter text-white mb-8 leading-tight">
                  Ready to dominate tech LinkedIn?
                </h2>
                <p className="text-lg md:text-xl text-gray-300 mb-12 leading-relaxed">
                  Stop shouting into the void. Join the elite group of developers
                  and executives who use editorial AI to build authority and drive
                  opportunities.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                  <GoogleSignInButton
                    testId="cta-login-btn"
                    className="px-8 py-4 text-lg shadow-xl hover:shadow-2xl"
                  />
                </div>
                <div className="mt-12 flex items-center justify-center gap-8 text-gray-400 text-sm">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" />
                    <span>14-day free trial</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    <span>No credit card required</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-muted/30 w-full py-12 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 px-6 md:px-12 max-w-7xl mx-auto">
            <div className="lg:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="PostWriter" className="w-7 h-7" />
                <span className="text-lg font-bold font-headline text-foreground">PostWriter</span>
              </div>
              <p className="text-muted-foreground text-sm mb-6">
                &copy; 2025 PostWriter. Powered by high-conversion editorial AI.
              </p>
            </div>
            <div>
              <h4 className="font-headline font-bold text-foreground mb-4 uppercase tracking-wider text-xs">
                Product
              </h4>
              <ul className="space-y-3">
                <li>
                  <a
                    href="#features"
                    className="text-muted-foreground hover:text-foreground underline decoration-primary/30 underline-offset-4 transition-all text-sm"
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a
                    href="#how-it-works"
                    className="text-muted-foreground hover:text-foreground underline decoration-primary/30 underline-offset-4 transition-all text-sm"
                  >
                    How It Works
                  </a>
                </li>
                <li>
                  <a
                    href="#testimonials"
                    className="text-muted-foreground hover:text-foreground underline decoration-primary/30 underline-offset-4 transition-all text-sm"
                  >
                    Testimonials
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-headline font-bold text-foreground mb-4 uppercase tracking-wider text-xs">
                Legal
              </h4>
              <ul className="space-y-3">
                <li>
                  <a
                    href="#"
                    className="text-muted-foreground hover:text-foreground underline decoration-primary/30 underline-offset-4 transition-all text-sm"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-muted-foreground hover:text-foreground underline decoration-primary/30 underline-offset-4 transition-all text-sm"
                  >
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-muted-foreground hover:text-foreground underline decoration-primary/30 underline-offset-4 transition-all text-sm"
                  >
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-headline font-bold text-foreground mb-4 uppercase tracking-wider text-xs">
                Connect
              </h4>
              <div className="flex gap-4">
                <a
                  className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-white transition-all"
                  href="#"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-white transition-all"
                  href="#"
                >
                  <Mail className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default LandingPage;
